import fs from "node:fs";
import path from "node:path";
import { assetSearchDirs } from "@skaile/workspaces/core";
import { aiResourceRoots } from "./paths.ts";

/**
 * A single skill discovered under a content root.
 *
 * @docLink cli/dev-guide#skill-walker
 */
export interface SkillEntry {
  name: string;
  path: string;
  skillPath: string;
  domain: string;
}

/**
 * Return all skill entries discovered across every {@link aiResourceRoots} root,
 * optionally filtered to a single domain. De-duplicated by skill name (first
 * root wins), so the bundled factory skills don't double up with a user library.
 *
 * Each root is walked in both layouts: **flat** (`<root>/skills/…`, e.g. the
 * bundled `factory-assets/` tree) and **domain-nested** (`<root>/<domain>/skills/…`).
 *
 * @param domain - Optional domain name to restrict the nested-layout search.
 * @returns Array of {@link SkillEntry} objects.
 * @docLink cli/dev-guide#skill-walker
 */
export function findSkills(domain?: string): SkillEntry[] {
  const found: SkillEntry[] = [];
  for (const root of aiResourceRoots()) {
    for (const { dir, domain: dirDomain } of assetSearchDirs(root, "skills")) {
      if (domain && dirDomain !== domain) continue;
      if (fs.existsSync(dir)) walkSkills(dir, dirDomain, found);
    }
  }
  const seen = new Set<string>();
  const deduped: SkillEntry[] = [];
  for (const s of found) {
    if (seen.has(s.name)) continue;
    seen.add(s.name);
    deduped.push(s);
  }
  return deduped;
}

/**
 * Recursively walk a directory tree looking for skill directories (those
 * containing a `SKILL.md` file) and push results into `out`.
 *
 * @param dir - Directory to walk.
 * @param domain - Domain label attached to every discovered entry.
 * @param out - Accumulator array mutated in-place.
 * @docLink cli/dev-guide#skill-walker
 */
export function walkSkills(dir: string, domain: string, out: SkillEntry[]): void {
  for (const entry of fs.readdirSync(dir, { withFileTypes: true })) {
    const full = path.join(dir, entry.name);
    if (entry.isDirectory()) {
      const skillMd = path.join(full, "SKILL.md");
      if (fs.existsSync(skillMd)) {
        out.push({ name: entry.name, path: full, skillPath: skillMd, domain });
      } else {
        walkSkills(full, domain, out);
      }
    }
  }
}
